import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns/esm';
import { parseISO } from 'date-fns';

import { useAppDispatch, useAppSelector } from '../../store/store';
import {
  selectScanCustomerStatus,
  Diagnostic,
  searchCheckIn,
  selectDiagnostics,
  selectScanCustomerCompanions,
  resetScanCustomerStatus,
  salesOrderStates,
} from '../../store/slices/checkIn';
import {
  SalesOrderInfo,
  selectSalesOrders,
  fetchSalesOrders,
  selectSalesOrdersStatus,
  selectServicesTranslations,
  fetchServicesTranslations,
} from '../../store/slices/salesOrders';
import {
  selectDiagnosticResults,
  selectDiagnosticResultsStatus,
  fetchDiagnosticResults,
  DiagnosticResult,
} from '../../store/slices/diagnosticResults';
import classes from './CheckIn.module.css';
import CheckInModal from './Modals/CheckInModal';
import Spinner from '../../components/UI/Spinner/Spinner';
import ScanSampleBarcodeModal from './Modals/ScanSampleBarcodeModal';
import EditSampleBarcodeModal from './Modals/EditSampleBarcodeModal';
import SelectProductModal from './Modals/SelectProductModal';
import {
  IServiceInfoByLocation,
  fetchServicesWithBiomarkerInfo,
  selectServiceInfosForLocation,
} from '../../store/slices/services';
import searchIcon from '../../assets/icons/search.svg';
import slidersIcon from '../../assets/icons/sliders.svg';
import extendCollapseIcon from '../../assets/icons/back-arrow.svg';
import extendCollapseIcon2 from '../../assets/icons/back-arrow2.svg';
import PrimaryButtonV2 from '../../components/UI/Buttons/PrimaryButtonV2';
import Modal from '../../components/UI/Modals/Modal';
import { ReportTable } from '../../components/UI/Layouts/ReportTable';
import CustomDropdown from '../../components/UI/Inputs/CustomDropdown';

import ConfirmPaymentModal from '../Orders/Modals/ConfirmPaymentModal';
import { client } from '../../networking/client';
import { toast } from 'react-toastify';
import { IAccountInfo, selectAccountInfo } from '../../store/slices/auth';
import { useSearchParams } from 'react-router-dom';
import { ReportsTableHeader } from '../../components/Headers/ReportsTableHeader';
import { buildTranslatedServicesMap, getAccountNumberFromEbaasPersonAccounts } from '../../utils/miscUtils';
import CustomPagination from '../../components/UI/CustomPagination/CustomPagination';
import { useTranslation } from '../../hooks/useTranslations';

const CheckIn: React.FC<any> = () => {
  const dispatch = useAppDispatch();
  const { translations } = useTranslation();
  const [searchEmailValue, setSearchEmailValue] = useState<string>('');
  const [isCheckingIn, setIsCheckingIn] = useState<boolean>(false);
  const [isScanningQrCode, setIsScanningQrCode] = useState<boolean>(false);
  const [isScanningSampleBarcode, setIsScanningSampleBarcode] = useState<boolean>(false);
  const [isOpenManual, setIsOpenManual] = useState<boolean>(false);
  const [isOpenSelectCompanion, setIsOpenSelectCompanion] = useState<boolean>(false);
  const searchCheckInStatus = useAppSelector<string>(selectScanCustomerStatus);
  const searchCheckInCompanions = useAppSelector(selectScanCustomerCompanions);
  const diagnostics = useAppSelector<Diagnostic[]>(selectDiagnostics);
  const serviceInfosByLocation = useAppSelector<IServiceInfoByLocation[]>(
    selectServiceInfosForLocation
  );
  const accountInfo = useAppSelector<IAccountInfo>(selectAccountInfo);

  const [selectedDiagnostic, setSelectedDiagnostic] = useState<Diagnostic>();
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [scanBarcodeValue, setScanBarcodeValue] = useState<string>('');
  const fetchSalesOrdersStatus = useAppSelector<string>(selectSalesOrdersStatus);
  const [isFetchingSalesOrders, setIsFetchingSalesOrders] = useState<boolean>(true);
  const salesOrders: SalesOrderInfo[] = useAppSelector<SalesOrderInfo[]>(selectSalesOrders);
  const fetchDiagnosticResultsStatus = useAppSelector<string>(selectDiagnosticResultsStatus);
  const diagnosticResults: DiagnosticResult[] =
    useAppSelector<DiagnosticResult[]>(selectDiagnosticResults);
  const [enhancedSalesOders, setEnhancedSalesOrders] = useState<SalesOrderInfo[]>([]);
  const [displaySalesOrders, setDisplaySalesOrders] = useState<SalesOrderInfo[]>([]);
  const [firstNameSearch] = useState<string>('');
  const [lastNameSearch] = useState<string>('');
  const [dateOfBirthSearch] = useState<string>('');
  const [testServiceSearch] = useState<string>('');
  const [sampleIdSearch] = useState<string>('');
  const [checkInTimeSearch] = useState<string>('');
  const [isEditingCheckIn, setIsEditingCheckIn] = useState<boolean>(false);
  const [salesOrderEditing, setSalesOrderEditing] = useState<SalesOrderInfo>();
  const [searchedUpcomingDiagnostics, setSearchedUpcomingDiagnostics] = useState<SalesOrderInfo[]>(
    []
  );
  const [selectedSalesOrderId, setSelectedSalesOrderId] = useState<string | undefined>();
  const [selectedSalesOrderItemId, setSelectedSalesOrderItemId] = useState<string | undefined>();
  const [showConfirmPaymentModal, setShowConfirmPaymentModal] = useState<boolean>(false);
  const [isConfirmingPaymentLoading, setIsConfirmingPaymentLoading] = useState<boolean>(false);
  //this is used if a diagnostic is unpaid, and the confirm payment is pressed
  //when the check-in modal is seen next to show that it is paid
  const [isDiagnosticPaid, setIsDiagnosticPaid] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const customerAccountNumber = searchParams.get('accountNumber');
  const salesOrderId = searchParams.get('salesOrderId');
  const salesOrderItemId = searchParams.get('salesOrderItemId');
  const [isExtended, setIsExtended] = useState(true);
  const [startDateValue, setStartDateValue] = useState<Date | undefined>();
  const [endDateValue, setEndDateValue] = useState<Date | undefined>();
  const [selectedDateColumn, setSelectedDateColumn] = useState<string>('booking_time');
  const servicesTranslations = useAppSelector(selectServicesTranslations);
  const lang = localStorage.getItem('lang') || 'en';


  const upcomingDiagnostics: SalesOrderInfo[] = useMemo(() => {
    if (salesOrders) {
      return salesOrders
        .filter(
          (salesOrder) =>
            (salesOrder.service_service_group?.service_group_id === 1 ||
              salesOrder.service_service_group?.service_group_id === 4) &&
            !salesOrder.work_order_service_diagnostic
        )
        .sort((a, b) => b.sales_order.sales_order_id - a.sales_order.sales_order_id);
    }
    return [];
  }, [salesOrders]);

  useEffect(() => {
    setSearchedUpcomingDiagnostics(upcomingDiagnostics);
  }, [upcomingDiagnostics]);

  useEffect(() => {
    dispatch(fetchSalesOrders());
    dispatch(fetchDiagnosticResults());
    dispatch(fetchServicesTranslations());
    dispatch(fetchServicesWithBiomarkerInfo());
    if (customerAccountNumber) {
      dispatch(
        searchCheckIn({
          accountNumber: customerAccountNumber,
        })
      );
    }
  }, [dispatch]);

  useEffect(() => {
    if (salesOrderId && salesOrderItemId && diagnostics) {
      const salesOrderIdNum = parseInt(salesOrderId);
      const salesOrderItemIdNum = parseInt(salesOrderItemId);
      const diag = diagnostics.find(
        (d) =>
          d.diagnostic.sales_order.sales_order_id === salesOrderIdNum &&
          d.diagnostic.sales_order_item.sales_order_item_id === salesOrderItemIdNum
      );
      setSelectedDiagnostic(diag);
      setScanBarcodeValue(
        `${diag?.diagnostic.sales_order.sales_order_id}${diag?.diagnostic.product.product_id}${diag?.diagnostic.sales_order_item.sales_order_item_id}`
      );
    }
  }, [diagnostics, salesOrderId, salesOrderItemId]);

  useEffect(() => {
    if (!selectedDiagnostic) setScanBarcodeValue('');
  }, [selectedDiagnostic]);

  useEffect(() => {
    if (isEditingCheckIn)
      setScanBarcodeValue(
        `${
          salesOrderEditing?.diagnostic_results
            ? salesOrderEditing?.diagnostic_results[0].sample_barcode
            : ''
        }`
      );
  }, [isEditingCheckIn]);

  useEffect(() => {
    if (fetchSalesOrdersStatus === 'pending') {
      setIsFetchingSalesOrders(true);
    } else if (fetchSalesOrdersStatus === 'fulfilled' || fetchSalesOrdersStatus === 'rejected') {
      setIsFetchingSalesOrders(false);
    }
  }, [fetchSalesOrdersStatus]);

  useEffect(() => {
  if (fetchDiagnosticResultsStatus === 'fulfilled' && fetchSalesOrdersStatus === 'fulfilled') {
    const tempoSalesOrders: SalesOrderInfo[] = [];
    for (const saleOrder of salesOrders.filter(
      (salesOrder) =>
        (salesOrder.service_service_group.service_group_id === 1 ||
          salesOrder.service_service_group.service_group_id === 4) &&
        salesOrder.work_order_service_diagnostic?.check_in_time &&
        salesOrder.work_order &&
        salesOrder.work_order.work_order_status_id === 1 &&
         // salesOrder.work_order_service_diagnostic?.outside_laboratory !== 1 &&

        (format(
          parseISO(
            salesOrder?.work_order_service_diagnostic?.check_in_time?.toString() ?? '01-01-1111'
          ),
          'dd.MM.yyyy'
        ) === format(new Date(), 'dd.MM.yyyy') ||
          salesOrder.service_service_group.service_group_id === 4)
    )) {

 const match = servicesTranslations
  ?.find((t) => Number(t.service_id) === Number(saleOrder.service.service_id))
  ?.translations.find(
    (tr) => tr.language.trim().toLowerCase() === lang.trim().toLowerCase()
  );


      const translatedServiceName = match?.service_name || '';

      if (
        saleOrder.work_order_service_diagnostic &&
        diagnosticResults.filter(
          (d) => d.work_order_id == saleOrder.work_order_service_diagnostic?.work_order_id
        ).length > 0
      ) {
        tempoSalesOrders.push({
          ...saleOrder,
          serviceAdditionalInfo: serviceInfosByLocation.find(
            (sibl) => sibl.service_id == saleOrder.service.service_id
          ),
          diagnostic_results: saleOrder.diagnostic_results
            ? [
                ...saleOrder.diagnostic_results,
                ...diagnosticResults.filter(
                  (d) =>
                    d.work_order_id == saleOrder.work_order_service_diagnostic?.work_order_id
                ),
              ]
            : [
                ...diagnosticResults.filter(
                  (d) =>
                    d.work_order_id == saleOrder.work_order_service_diagnostic?.work_order_id
                ),
              ],
          translated_service_name: translatedServiceName,
        });
      } else {
        tempoSalesOrders.push({
          ...saleOrder,
          serviceAdditionalInfo: serviceInfosByLocation.find(
            (sibl) => sibl.service_id == saleOrder.service.service_id
          ),
          translated_service_name: translatedServiceName,
        });
      }
    }

    setEnhancedSalesOrders(tempoSalesOrders);
  }
},[fetchDiagnosticResultsStatus, fetchSalesOrdersStatus, diagnosticResults, salesOrders , lang ,servicesTranslations ]);


  useEffect(() => {
    setDisplaySalesOrders([...enhancedSalesOders]);
  }, [enhancedSalesOders]);

  useEffect(() => {
    if (searchCheckInStatus === 'pending') {
      setIsSearching(true);
    } else if (searchCheckInStatus === 'fulfilled') {
      setIsCheckingIn(true);
      setIsOpenManual(false);
      dispatch(resetScanCustomerStatus());
      setIsSearching(false);
      if (selectedSalesOrderId && selectedSalesOrderItemId) {
        const diag = diagnostics.find(
          (d) =>
            d.diagnostic.sales_order.sales_order_id.toString() == selectedSalesOrderId &&
            d.diagnostic.sales_order_item?.sales_order_item_id?.toString() ==
              selectedSalesOrderItemId
        );
        setSelectedDiagnostic(diag);
        setScanBarcodeValue(
          `${diag?.diagnostic.sales_order.sales_order_id}${diag?.diagnostic.product.product_id}`
        );
      }
      setSelectedSalesOrderId(undefined);
      setSelectedSalesOrderItemId(undefined);
    } else if (searchCheckInStatus === 'rejected') {
      if (searchCheckInCompanions.length > 0) {
        setIsOpenSelectCompanion(true);
        setIsOpenManual(false);
        dispatch(resetScanCustomerStatus());
      }
      setSelectedSalesOrderId(undefined);
      setSelectedSalesOrderItemId(undefined);
      setIsSearching(false);
    }
  }, [searchCheckInStatus, dispatch, diagnostics, searchCheckInCompanions]);

  useEffect(() => {
    let tempoSalesOrders = [...enhancedSalesOders];
    if (firstNameSearch.trim().length > 0)
      tempoSalesOrders = tempoSalesOrders.filter((salesOrder) =>
        salesOrder.customer_person.person_firstname
          .toLowerCase()
          .includes(firstNameSearch.toLocaleLowerCase())
      );
    if (lastNameSearch.trim().length > 0)
      tempoSalesOrders = tempoSalesOrders.filter((salesOrder) =>
        salesOrder.customer_person.person_lastname
          .toLowerCase()
          .includes(lastNameSearch.toLocaleLowerCase())
      );
    if (dateOfBirthSearch.trim().length > 0)
      tempoSalesOrders = tempoSalesOrders.filter((salesOrder) =>
        salesOrder.customer_person.person_birthdate
          ?.toString()
          .toLowerCase()
          .includes(dateOfBirthSearch.toLocaleLowerCase())
      );
    if (testServiceSearch.trim().length > 0)
      tempoSalesOrders = tempoSalesOrders.filter((salesOrder) =>
        salesOrder.product.product_name
          .toLowerCase()
          .includes(testServiceSearch.toLocaleLowerCase())
      );
    if (sampleIdSearch.trim().length > 0)
      tempoSalesOrders = tempoSalesOrders.filter(
        (salesOrder) =>
          salesOrder?.diagnostic_results &&
          salesOrder?.diagnostic_results[0]?.sample_barcode &&
          salesOrder?.diagnostic_results[0]?.sample_barcode
            .toLowerCase()
            .includes(sampleIdSearch.toLocaleLowerCase())
      );
    if (checkInTimeSearch.trim().length > 0)
      tempoSalesOrders = tempoSalesOrders.filter((salesOrder) =>
        format(
          parseISO(
            salesOrder?.work_order_service_diagnostic?.check_in_time?.toString() ?? '01-01-1111'
          ),
          'dd.MM.yyyy; HH:mm'
        )
          .toLowerCase()
          .includes(checkInTimeSearch.toLocaleLowerCase())
      );
    setDisplaySalesOrders([...tempoSalesOrders]);
  }, [
    firstNameSearch,
    lastNameSearch,
    dateOfBirthSearch,
    testServiceSearch,
    sampleIdSearch,
    checkInTimeSearch,
    enhancedSalesOders,
  ]);

  useEffect(() => {
    onSearchCustomerHandler();
  }, [searchEmailValue, startDateValue, endDateValue, selectedDateColumn]);

  const maxDate = useMemo(() => {
    const dates = upcomingDiagnostics
      .filter((salesOrder) => salesOrder.sales_order_appointment?.start)
      .map((salesOrder) => new Date(salesOrder.sales_order_appointment?.start ?? '').getTime());
    return new Date(Math.max.apply(null, dates.length > 0 ? dates : [new Date().getTime()]));
  }, [upcomingDiagnostics]);

  const onScanCustomer = (email?: string, accountNumber?: string) => {
    if (email && email.trim().length > 0) {
      setSearchEmailValue('');
      dispatch(
        searchCheckIn({
          patientEmail: email.trim(),
        })
      );
    } else if (accountNumber) {
      dispatch(
        searchCheckIn({
          accountNumber: accountNumber,
        })
      );
    }
  };

  const onSearchCustomerHandler = () => {
    const searchNameSurname = searchEmailValue.toLowerCase().replace(/\s+/g, '');
    let searchedDiagnostics = upcomingDiagnostics;
    if (searchNameSurname.trim() !== '') {
      searchedDiagnostics = upcomingDiagnostics.filter((upcomingDiagnostic) => {
        return `${upcomingDiagnostic.customer_person.person_firstname}${
          upcomingDiagnostic.customer_person.person_lastname
        }${upcomingDiagnostic.customer_person.person_email ?? ''}`
          .toLowerCase()
          .replace(/\s+/g, '')
          .includes(searchNameSurname);
      });
    }

    if (startDateValue && endDateValue) {
      searchedDiagnostics = searchedDiagnostics.filter((salesOrder) => {
        if (
          !salesOrder.sales_order_appointment?.start &&
          selectedDateColumn === 'appointment_time'
        ) {
          return false;
        }
        const date = new Date(
          selectedDateColumn === 'appointment_time'
            ? salesOrder?.sales_order_appointment?.start ?? new Date()
            : salesOrder?.sales_order.datetime_create
        ).getTime();
        return (
          new Date(startDateValue).getTime() <= date &&
          new Date(endDateValue).getTime() + 86400000 > date
        );
      });
    }
    setSearchedUpcomingDiagnostics(searchedDiagnostics);
    if (!searchedDiagnostics || searchedDiagnostics.length === 0) {
      toast.warn(translations('no.customer.with.upcoming'), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  };

  const onCustomerSelectHandler = async (salesOrder: SalesOrderInfo) => {
    dispatch(
      salesOrderStates({
        accountNumber: accountInfo.account_number,
        personAccount: salesOrder?.customer_person.person_account_number,
      })
    );
    setSearchedUpcomingDiagnostics(upcomingDiagnostics);
    setSearchEmailValue('');
    setIsOpenManual(false);
    let personAccountNumber: string = '';
    if (!salesOrder.customer_person.person_account_number) {
      // adding 3 seconds delay because account number isn't created immediately after user is registered
      await new Promise((resp) => setTimeout(resp, 3000));
      const ebaasPersonInfo = await client.get(
        `/core/person/accounts/${salesOrder.customer_person.person_external_id}`
      );
      const accountNumberFetched = getAccountNumberFromEbaasPersonAccounts(ebaasPersonInfo);
      const response = await client.put(
        `/healthpoint/person/accountNumber/${salesOrder.customer_person.person_external_id}/${accountNumberFetched}`
      );
      if (response.data?.person) {
        personAccountNumber = response.data?.person.person_account_number;
      }
    }
    setSelectedSalesOrderId(salesOrder.sales_order.sales_order_id.toString());
    setSelectedSalesOrderItemId(salesOrder.sales_order_item.sales_order_item_id.toString());
    dispatch(
      searchCheckIn({
        accountNumber: salesOrder.customer_person.person_account_number ?? personAccountNumber,
      })
    );
  };

  const onConfirmPaymentHandler = async () => {
    try {
      setIsConfirmingPaymentLoading(true);
      await client.put(
        `/healthpoint/paid-sales-order/${selectedDiagnostic?.diagnostic.sales_order.sales_order_id}`
      );
      await dispatch(fetchSalesOrders());
      setShowConfirmPaymentModal(false);
      setIsDiagnosticPaid(true);
      toast.success(translations('success').toString(), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    } catch (_) {
      toast.error(translations('error').toString(), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    } finally {
      setIsConfirmingPaymentLoading(false);
    }
  };

  
  return (
    <div className={classes.container}>
      {isExtended && (
        <div className={classes.checkInsContainer}>
          <div className={classes.checkInTitleContainer}>
            <div className={classes.checkInsTitleText}>{translations('customer.check.in')}</div>
            <img
              src={isExtended ? extendCollapseIcon2 : extendCollapseIcon}
              alt="extend"
              onClick={() => setIsExtended(!isExtended)}
              className={`${
                isExtended ? classes.collapseIconAbsolute : classes.extendIconAbsolute
              }`}
            />
          </div>
          <div className={classes.dividerHorizontal} />
          {!isSearching ? (
            <>
              <div className={classes.checkInSection}>
                <div className={classes.checkInSectionText}>{translations('search.customer')}</div>
                <PrimaryButtonV2
                  text={translations('manual.check.in')}
                  containerStyle={{
                    width: '100%',
                    marginTop: 10,
                  }}
                  buttonStyle={{
                    width: '100%',
                    height: 50,
                    textTransform: 'uppercase',
                    borderRadius: 8,
                    fontWeight: 600,
                    fontSize: 14,
                  }}
                  disabled={false}
                  onClick={() => setIsOpenManual(true)}
                />
              </div>
              <div className={classes.dividerHorizontal} />
              <div className={classes.checkInSection}>
                <div className={classes.checkInSectionText}>{translations('scan.code')}</div>
                <PrimaryButtonV2
                  text={translations('scan.qr.code')}
                  containerStyle={{
                    width: '100%',
                    marginTop: 10,
                  }}
                  buttonStyle={{
                    width: '100%',
                    height: 50,
                    textTransform: 'uppercase',
                    borderRadius: 8,
                    fontWeight: 600,
                    fontSize: 14,
                  }}
                  disabled={false}
                  onClick={() => setIsScanningQrCode(true)}
                />
              </div>
            </>
          ) : (
            <Spinner />
          )}
        </div>
      )}
      <div className={classes.checkInsTableContainer}>
        <div className={classes.checkInsTitle}>
          {!isExtended && (
            <img
              src={isExtended ? extendCollapseIcon2 : extendCollapseIcon}
              alt="extend"
              onClick={() => setIsExtended(!isExtended)}
              className={classes.extendIconAbsolute}
            />
          )}
          <div className={classes.checkInsTitleText}>{translations('checked.in')}</div>
          <div className={classes.checkInsTableIconsContainer}>
            <img src={searchIcon} className={classes.checkInsTableIcons} alt={'search icon'} />
            <img src={slidersIcon} className={classes.checkInsTableIcons} alt={'sliders icon'} />
          </div>
        </div>
        <div className={classes.dividerHorizontal} />
        <div>
          {isFetchingSalesOrders ? (
            <Spinner />
          ) : (
            <CustomPagination itemsPerPage={10} items={displaySalesOrders}>
              {(paginatedItems: any[]) => (
                <ReportTable
                  columns={[
                    {
                      name: 'ID',
                      path: 'sales_order.sales_order_id',
                      type: 'ID',
                    },
                    {
                      name: translations('check.time').toString(),
                      path: 'work_order_service_diagnostic.check_in_time',
                      type: 'DATETIME',
                    },
                    {
                      name: translations('last_name').toString(),
                      path: 'customer_person.person_lastname',
                      type: 'TEXT',
                    },
                    {
                      name: translations('first_name').toString(),
                      path: 'customer_person.person_firstname',
                      type: 'TEXT',
                    },
                    {
                      name: translations('date_of_birth').toString(),
                      path: 'customer_person.person_birthdate',
                      type: 'DATE',
                    },
                    {
                      name: translations('test.service').toString(),
                      path: 'translated_service_name',
                      type: 'TEXT',
                    },
                    {
                      name: translations('sample.id').toString(),
                      path: 'diagnostic_results.0.sample_barcode',
                      type: 'TEXT',
                    },
                    {
                      name: translations('laboratory'),
                      path: 'serviceAdditionalInfo.business_partner_name1',
                      type: 'TEXT',
                    },
                  ]}
                  rows={paginatedItems}
                  actions={[
                    {
                      displayName: translations('edit').toString(),
                      onClick: (row) => {
                        setSalesOrderEditing(row as SalesOrderInfo);
                        setIsEditingCheckIn(true);
                      },
                    },
                  ]}
                  isInCheckIn={true}
                />
              )}
            </CustomPagination>
          )}
        </div>
      </div>
      <SelectProductModal
        isOpen={isCheckingIn && selectedDiagnostic === undefined}
        onClose={() => setIsCheckingIn(false)}
        diagnostics={diagnostics}
        selectedDiagnostic={selectedDiagnostic}
        onChange={(e: any) => {
          setSelectedDiagnostic(
            diagnostics.find((d) => d.diagnostic.sales_order.sales_order_id == e.target.value)
          );
        }}
        onSelectFirstProduct={() => setSelectedDiagnostic(diagnostics[0])}
      />
      {!isScanningSampleBarcode &&
        isCheckingIn &&
        selectedDiagnostic !== undefined &&
        !showConfirmPaymentModal && (
          <CheckInModal
            isOpen={isCheckingIn && selectedDiagnostic !== undefined}
            onClose={() => {
              setSelectedDiagnostic(undefined);
              setIsCheckingIn(false);
              setIsDiagnosticPaid(false);
            }}
            diagnostic={selectedDiagnostic}
            onScanClick={() => {
              setIsScanningSampleBarcode(true);
            }}
            scanValue={scanBarcodeValue}
            onChangeScanValue={(e: any) => setScanBarcodeValue(e.target.value)}
            onBackButtonClick={() => setSelectedDiagnostic(undefined)}
            onConfirmPayment={() => setShowConfirmPaymentModal(true)}
            showAsPaid={isDiagnosticPaid}
          />
        )}
      {isEditingCheckIn && (
        <EditSampleBarcodeModal
          isOpen={isEditingCheckIn}
          onClose={() => setIsEditingCheckIn(false)}
          onScanClick={() => setIsScanningSampleBarcode(true)}
          scanValue={scanBarcodeValue}
          onChangeScanValue={(e: any) => setScanBarcodeValue(e.target.value)}
          sampleTakenId={
            salesOrderEditing?.diagnostic_results
              ? salesOrderEditing?.diagnostic_results[0].sample_taken_id ?? 0
              : 0
          }
          selectedSalesOrder={salesOrderEditing}
        />
      )}
      <ScanSampleBarcodeModal
        isOpen={isScanningSampleBarcode}
        onClose={() => setIsScanningSampleBarcode(false)}
        onSuccessScan={(decodedText: any) => {
          if (decodedText) setScanBarcodeValue(decodedText);
          setIsScanningSampleBarcode(false);
        }}
        openManual={() => {
          if (!isCheckingIn) {
            setIsOpenManual(true);
          }
        }}
      />
      <ScanSampleBarcodeModal
        isOpen={isScanningQrCode}
        onClose={() => setIsScanningQrCode(false)}
        onSuccessScan={(decodedText: string) => {
          let a = decodedText.split('\r\n');
          let longestString = '';
          for (let i = 0; i < a.length; i++) {
            if (a[i].trim().includes('DE') && a[i].trim().length >= 20) longestString = a[i].trim();
          }
          if (longestString && longestString.length >= 10) {
            let accountNumber: string = longestString.slice(-10);
            onScanCustomer(undefined, accountNumber);
          }
          setIsScanningQrCode(false);
        }}
        openManual={() => {
          if (!isCheckingIn) {
            setIsOpenManual(true);
          }
        }}
      />
      <Modal
        isOpen={isOpenManual}
        containerPadding={'20px'}
        onClose={() => {
          setSearchEmailValue('');
          setIsOpenManual(false);
        }}
        header={translations('search.manually').toString()}
        divider
        width="94%"
        maxWidth="1200px"
      >
        <div className={classes.modalContent}>
          <ReportsTableHeader
            tabs={[]}
            firstNameSearch={searchEmailValue}
            setFirstNameSearch={setSearchEmailValue}
            searchPlaceholder={'search.customer'}
            setDateBegin={setStartDateValue}
            dateBegin={startDateValue}
            setDateEnd={setEndDateValue}
            selectedDateColumn={selectedDateColumn}
            setDateColumn={setSelectedDateColumn}
            maxAppointmentDate={maxDate}
            dateEnd={endDateValue}
          />
          <CustomPagination itemsPerPage={10} items={searchedUpcomingDiagnostics}>
            {(paginatedItems: any[]) => (
              <ReportTable
                columns={[
                  {
                    name: 'ID',
                    path: 'sales_order.sales_order_id',
                    type: 'ID',
                  },
                  {
                    name: translations('first_name').toString(),
                    path: 'customer_person.person_firstname',
                    type: 'TEXT',
                  },
                  {
                    name: translations('last_name').toString(),
                    path: 'customer_person.person_lastname',
                    type: 'TEXT',
                  },
                  {
                    name: translations('date_of_birth').toString(),
                    path: 'customer_person.person_birthdate',
                    type: 'DATE',
                  },
                  {
                    name: translations('test.service').toString(),
                    path: 'product.product_name',
                    type: 'TEXT',
                  },
                  {
                    name: translations('appointment').toString(),
                    path: 'sales_order_appointment.start',
                    type: 'APPOINTMENT',
                  },
                  {
                    name: translations('created').toString(),
                    path: 'sales_order.datetime_create',
                    type: 'DATETIME',
                  },
                ]}
                rows={paginatedItems}
                actions={[
                  {
                    displayName: translations('select.customer').toString(),
                    displayPrimaryButton: true,
                    onClick: (row) => {
                      onCustomerSelectHandler(row as SalesOrderInfo);
                    },
                  },
                ]}
                isInCheckIn={true}
              />
            )}
          </CustomPagination>
        </div>
      </Modal>
      {isOpenSelectCompanion && (
        <Modal
          isOpen={isOpenSelectCompanion}
          containerPadding={'80px 60px 40px 60px'}
          onClose={() => setIsOpenSelectCompanion(false)}
          header={translations('select.companion').toString()}
          divider
        >
          <div>
            <CustomDropdown
              label={translations('select.companion')}
              placeholder={translations('companion')}
              onChange={(e) => {
                onScanCustomer(undefined, e.target.value);
                setIsOpenSelectCompanion(false);
              }}
              options={searchCheckInCompanions.map((companion) => {
                return {
                  id: companion.person_account_number,
                  label: `${companion.person_firstname} ${companion.person_lastname}`,
                };
              })}
            />
          </div>
        </Modal>
      )}
      {showConfirmPaymentModal && (
        <ConfirmPaymentModal
          isOpen={showConfirmPaymentModal}
          onClose={() => setShowConfirmPaymentModal(false)}
          productPrice={
            selectedDiagnostic?.diagnostic?.sales_order?.amount
              ? selectedDiagnostic.diagnostic.sales_order.amount * 100
              : 0
          }
          confirmPayment={onConfirmPaymentHandler}
          isLoading={isConfirmingPaymentLoading}
        />
      )}
    </div>
  );
};

export default CheckIn;
